<script setup lang="ts">
  import * as v from 'valibot'
  import type { Form, FormSubmitEvent } from '#ui/types'

  // Idioma
  const { locale, t } = useI18n()

  // Props
  defineProps({
    showRecover: {
      type: Boolean,
      default: true
    },
    submitLabel: {
      type: String,
      default: ''
    },
    showRegisterLink: {
      type: Boolean,
      default: true
    },
    showSeparator: {
      type: Boolean,
      default: true
    },
    showSocialButtons: {
      type: Boolean,
      default: true
    },
    linkRegister: {
      type: Boolean,
      default: true
    },
    hotelId: {
      type: Number,
      default: null
    }
  })

  // Emits
  const emit = defineEmits(['login-success', 'login-error', 'show-register'])

  // Validation
  const schema = v.object({
    email: v.pipe(
      v.string(),
      v.email(t('errors.login.email'))
    ),
    password: v.pipe(
      v.string(),
      v.minLength(8, t('errors.login.password'))
    )
  })

  type Schema = v.InferOutput<typeof schema>

  const state = reactive({
    email: '',
    password: ''
  })

  const form = ref<Form<Schema>>()
  const error_message = ref<string>('')
  const isLoading = ref(false)
  const showPassword = ref(false)

  async function onSubmit(event: FormSubmitEvent<Schema>) {
    isLoading.value = true
    error_message.value = ''
    form.value!.clear()

    try {
      const data = await $fetch<{ success: boolean; data: OAuthUser }>(`/auth/byhours/login`, {
        method: 'POST',
        body: {
          email: event.data.email,
          password: event.data.password,
          locale: locale.value
        }
      })

      const { fetch } = useUserSession()
      await fetch()

      emit('login-success', data)
    } catch (e: unknown) {
      const err = e as { data?: { statusMessage?: string }; statusMessage?: string }
      error_message.value = err?.data?.statusMessage || err?.statusMessage || ''
      emit('login-error', error_message.value)
    } finally {
      isLoading.value = false
    }
  }

  watch(state, (value) => {
    if(!value.email && !value.password) form.value!.clear()
    error_message.value = ''
  })

</script>

<template>
  <div class="w-full flex flex-col gap-4">
    <UForm ref="form" :schema="schema" class="flex flex-col space-y-4" :state="state" @submit="onSubmit">
      
      <UFormField name="email">
        <UInput 
          v-model="state.email" 
          autocomplete="username" 
          :placeholder="t('labels.login.placeholder.email')" 
          size="xl" 
        />
      </UFormField>

      <UFormField name="password">
        <UInput 
          v-model="state.password" 
          autocomplete="current-password" 
          :type="showPassword ? 'text' : 'password'"
          :placeholder="t('labels.login.placeholder.password')" 
          size="xl"
          :ui="{ trailing: 'pe-1' }"
        >
          <template #trailing>
            <UButton
              color="neutral"
              variant="link"
              size="sm"
              :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="showPassword ? $t('labels.aria.hidePassword') : $t('labels.aria.showPassword')"
              :aria-pressed="showPassword"
              aria-controls="password"
              @click="() => { showPassword = !showPassword }"
            />
          </template>
        </UInput>
      </UFormField>

      <UAlert
        v-if="error_message"
        icon="i-lucide-alert-circle"
        :title="error_message"
        color="error"
        variant="soft"
      />

      <UFormField v-if="showRecover" name="remember">
        <ULink :to="`/${locale}/recover`" class="text-primary">{{ t('labels.login.password') }}</ULink>
      </UFormField>

      <UButton 
        type="submit"
        class="justify-center bg-byhours-gradient"
        variant="solid"
        size="xl"
        :loading="isLoading"
        :disabled="isLoading"
      >
        {{ submitLabel || t('labels.login.session') }}
      </UButton>

    </UForm>

    <div v-if="showRegisterLink" class="flex items-center gap-2 w-full justify-center text-sm mt-4">
      <span>{{ t('labels.login.noAccount') }}</span>
      <ULink v-if="linkRegister" :to="`/${locale}/register`" class="text-primary">{{ t('labels.login.registerLabel') }}</ULink>
      <UButton 
        v-else
        color="primary"
        variant="link"
        size="md"
        @click="emit('show-register')"
      >
        {{ t('labels.login.registerLabel') }}
      </UButton>
    </div>

    <USeparator v-if="showSeparator" :label="t('labels.login.o')" class="my-4" />

    <LoginButtons v-if="showSocialButtons" :hotel-id="hotelId" />

  </div>
</template> 